import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, openModal } from "risa-oasis-ui_v2";
import { getNccnGuideLines } from "../../api/postCall/nccnGuidelines";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import DocumentsModal from "../../components/modals/documentsModal/documentsModal";
import DocViewerWithSideNavigation from "../../components/modals/docViewerWithSideNavigation/docViewerWithSideNavigation";
import PdfRender from "../../components/pdfRender/pdfRender";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../enums/eventsData";
import { MedicalPaOrdersAuthStatus } from "../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../enums/modalId";
import { getMedicalPaOrderDetailsFor } from "../../redux/slice/medicalPa/singleMedicalPaOrder";
import { closeModal, setOpenedModalId } from "../../redux/slice/modalSlice";
import { AppDispatch } from "../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import {
  determineGuidelineType,
  getGuidelineButtonText,
} from "../medicalNecessityReview/utils/guidelineSelector";
import { getAllJCodes } from "../singleOrderLayout/utils/existingDocsToLs";
import {
  useDocumentManagement,
  useFaxSubmission,
  useFinalDocumentsData,
  useFormSubmission,
  useMergePdfs,
} from "./hooks";
import {
  logManageAttachmentsClick,
  logSubmissionInitiated,
  logSubmitFormClick,
  logUploadingAttachmentCompleted,
} from "./utils/bigQueryLogger";
import { handleFaxSubmission } from "./utils/handleFaxSubmission";

const FinalDocuments = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [guidelineItems, setGuidelineItems] = useState<any[]>([]);

  const orgId = getOrgIdForFetchExternalWorklist();

  // Redux selectors
  const {
    openedModalId,
    singleMedicalPaOrder,
    unsubscribe,
    isLoadingSingleMedicalPaOrder,
    singleMedicalPaFormConfig,
    user,
    downloadURL,
    progress,
  } = useFinalDocumentsData();

  // Determine guideline type based on insurance
  const guidelineType = useMemo(() => {
    const insuranceType =
      singleMedicalPaOrder?.coverage?.primary?.payer_name || "";
    return determineGuidelineType(insuranceType);
  }, [singleMedicalPaOrder]);

  // Fetch guideline items when order ID is available
  useEffect(() => {
    const fetchGuidelineItems = async () => {
      if (!id) return;

      try {
        console.log("📘 Fetching NCCN guidelines for final document:", id);
        const nccnResponse = await getNccnGuideLines({ order_id: id });

        if (nccnResponse?.data && nccnResponse.data.length > 0) {
          console.log(
            `✅ Received ${nccnResponse.data.length} guideline item(s)`,
            nccnResponse.data,
          );
          setGuidelineItems(nccnResponse.data as any);
        }
      } catch (error) {
        console.warn("⚠️ Could not fetch NCCN guidelines:", error);
      }
    };

    fetchGuidelineItems();
  }, [id]);

  const handleViewGuidelines = () => {
    if (guidelineItems && guidelineItems.length > 0) {
      dispatch(
        setOpenedModalId({
          id: ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION,
          metaData: {
            items: guidelineItems,
          },
        }),
      );
    } else {
      console.log("📘 No guideline items available");
    }
  };

  // Document management hook
  const {
    patientName,
    isLoading,
    fileNames,
    documents,
    setFileNames,
    setDocuments,
    downloadPdf,
  } = useDocumentManagement({
    singleMedicalPaOrder: singleMedicalPaOrder ?? null,
    isLoadingSingleMedicalPaOrder: isLoadingSingleMedicalPaOrder ?? false,
  });

  // PDF merge hook - merges multiple PDFs into one
  const { mergedFile, isMerging, mergePdfsFromBase64 } = useMergePdfs();

  // Fax submission hook
  const { makeSendCallAndUpdateFirestore } = useFaxSubmission({
    orderId: id,
    singleMedicalPaOrder: singleMedicalPaOrder ?? null,
    orgId: orgId ?? "",
  });

  // Form submission hook
  const {
    isSubmitting,
    setIsSubmitting,
    updateClinicalDocument,
    submitNormalFlow,
    finalButtonText: normalFlowButtonText,
  } = useFormSubmission({
    singleMedicalPaOrder: singleMedicalPaOrder ?? null,
    orderId: id,
    documents,
    downloadURL: downloadURL ?? null,
    progress,
  });

  // Fetch order details on mount
  useEffect(() => {
    if (id) dispatch(getMedicalPaOrderDetailsFor(id));
  }, [id, dispatch]);

  // Handle file upload completion
  useEffect(() => {
    if (downloadURL) {
      logUploadingAttachmentCompleted({
        singleMedicalPaOrder: singleMedicalPaOrder ?? null,
        orgId: orgId ?? "",
        userId: user?.id ?? "",
        userEmail: user?.email ?? "",
      });

      if (location.state?.faxPdfLink) {
        handleFaxSubmission(
          id ?? "",
          location.state.faxPdfLink,
          singleMedicalPaFormConfig?.id ?? "",
          setIsSubmitting,
          navigate,
          makeSendCallAndUpdateFirestore,
        );
      } else {
        updateClinicalDocument();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadURL]);

  // Handle modal opening
  useEffect(() => {
    if (openedModalId === ModalId.FINAL_DOCUMENT_MODAL) {
      openModal(ModalId.FINAL_DOCUMENT_MODAL);
    }
    if (openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION) {
      openModal(ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION);
    }
  }, [openedModalId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [unsubscribe]);

  const handleSubmitClick = async () => {
    logSubmitFormClick({
      singleMedicalPaOrder: singleMedicalPaOrder ?? null,
      orgId: orgId ?? "",
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      additionalData: {
        form_id: singleMedicalPaOrder?.selected_form_id_for_submission ?? "",
        status: singleMedicalPaOrder?.status?.submission ?? "",
        j_codes:
          getAllJCodes(singleMedicalPaOrder ?? {})
            ?.map((jCode) => jCode?.j_code ?? "")
            .join(",") ?? "",
        valid_hpcs_codes:
          singleMedicalPaOrder?.hpcs_codes
            ?.filter(
              (hpcs) =>
                hpcs.auth_status === MedicalPaOrdersAuthStatus.AuthRequired,
            )
            ?.map((hpcs) => hpcs.j_code)
            ?.join(",") ?? "",
      },
    });

    logSubmissionInitiated({
      singleMedicalPaOrder: singleMedicalPaOrder ?? null,
      orgId: orgId ?? "",
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      additionalData: {
        screen_name: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
        payer_name: singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
        regimen_name: singleMedicalPaOrder?.payload?.regimen_name ?? "",
        form_id: singleMedicalPaOrder?.selected_form_id_for_submission ?? "",

        status: singleMedicalPaOrder?.status?.submission ?? "",
        j_codes:
          getAllJCodes(singleMedicalPaOrder ?? {})
            ?.map((jCode) => jCode?.j_code ?? "")
            .join(",") ?? "",
        valid_hpcs_codes:
          singleMedicalPaOrder?.hpcs_codes
            ?.filter(
              (hpcs) =>
                hpcs.auth_status === MedicalPaOrdersAuthStatus.AuthRequired,
            )
            ?.map((hpcs) => hpcs.j_code)
            ?.join(",") ?? "",
      },
    });

    await submitNormalFlow();
  };

  const handleManageAttachmentsClick = () => {
    dispatch(setOpenedModalId({ id: ModalId.FINAL_DOCUMENT_MODAL }));

    logManageAttachmentsClick({
      singleMedicalPaOrder: singleMedicalPaOrder ?? null,
      orgId: orgId ?? "",
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      additionalData: {
        order_status: singleMedicalPaOrder?.status?.financial_review ?? "",
        auth_status: singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
      },
    });
  };

  return (
    <div className="flex h-full w-screen flex-col items-center justify-center bg-primaryGray-16 p-2">
      <div className="flex h-full w-1/2 flex-1 flex-col gap-2 overflow-y-hidden rounded-md bg-white p-4">
        <div className="flex w-full flex-row items-center gap-2 p-2">
          <div className="text-h16 flex items-center justify-center font-semibold">
            {patientName}
          </div>
          <div>({singleMedicalPaOrder?.demographics?.patient_mrn ?? ""})</div>

          <div className="ml-auto flex items-center gap-2">
            <div
              className="cursor-pointer text-sm font-semibold text-tertiaryBlue-4 hover:text-tertiaryBlue-6"
              onClick={handleViewGuidelines}
            >
              {getGuidelineButtonText(guidelineType)}
            </div>
            <Button
              buttonType="secondary"
              size="small"
              disabled={false}
              onClick={handleManageAttachmentsClick}
            >
              Manage Attachments
            </Button>
          </div>
        </div>

        <div className="flex-1" style={{ overflow: "hidden" }}>
          <div
            className="h-full overflow-y-auto rounded-xl border border-primaryGray-14 bg-lightGray p-5"
            style={{
              height: "100%",
              overflow: "hidden",
            }}
          >
            {isLoading ? (
              <div className="flex h-full w-full items-center justify-center">
                <LoaderMessage message="Fetching clinical document..." />
              </div>
            ) : (
              <div className="h-full overflow-y-auto rounded-xl bg-white">
                {!isLoadingSingleMedicalPaOrder && mergedFile ? (
                  <PdfRender
                    key="merged-pdf"
                    file={mergedFile ?? undefined}
                    hideRotateButtons={true}
                    hideZoomButtons={true}
                  />
                ) : (
                  documents &&
                  documents.length > 0 &&
                  documents.map((document, index) => (
                    <PdfRender
                      key={index}
                      file={document}
                      hideRotateButtons={true}
                      hideZoomButtons={true}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-row items-center justify-between py-2">
          <div
            className={`text-sm font-bold ${
              isLoading
                ? "cursor-not-allowed text-primaryGray-12"
                : "text-tertiaryBlue-4 hover:cursor-pointer hover:text-tertiaryBlue-6"
            }`}
            onClick={isLoading ? undefined : downloadPdf}
          >
            Save as Draft
          </div>
          <div className="actions-buttons flex gap-4">
            <Button
              disabled={false}
              children={"Go Back"}
              onClick={() => {
                navigate(-1);
              }}
              buttonType={"secondary"}
              size={"medium"}
            />
            <Button
              buttonType="primary"
              size="medium"
              disabled={isLoading || isSubmitting}
              onClick={handleSubmitClick}
            >
              {normalFlowButtonText}
            </Button>
          </div>
        </div>
      </div>
      {openedModalId === ModalId.FINAL_DOCUMENT_MODAL && (
        <DocumentsModal
          onClose={() => dispatch(closeModal())}
          documentsName={fileNames}
          documentsList={documents}
          id={singleMedicalPaOrder?.id ?? ""}
          newDocumentNames={(urls) => setFileNames(urls)}
          newFiles={(files) => setDocuments(files)}
          patientId={singleMedicalPaOrder?.patient_id ?? ""}
          orderId={singleMedicalPaOrder?.id ?? ""}
          orderData={singleMedicalPaOrder ?? {}}
          onMergePdfs={mergePdfsFromBase64}
          isMerging={isMerging}
        />
      )}
      {openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION && (
        <DocViewerWithSideNavigation
          screenName={ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN}
          shouldLogEvent={true}
          modalName={ModalNamesForBigQuery.NCCN_GUIDELINES_MODAL}
        />
      )}
    </div>
  );
};

export default FinalDocuments;
